/**
 * Stale / unverified marker for a persisted Breakout Score (audit AUD-001/003).
 *
 * Stale or freshness-unverified scores stay visible (read-only ranking and
 * history) but are NOT actionable; this badge says so explicitly. Renders
 * nothing for a current, verified, actionable score.
 */
import { Chip, Tooltip } from "@mui/material";

export interface FreshnessFields {
  stale?: boolean;
  actionable?: boolean;
  actionable_reason?: string | null;
  freshness_state?: string | null;
  calendar_coverage?: string | null;
  expected_completed_session?: string | null;
}

const REASON_TEXT: Record<string, string> = {
  stale_score: "This score is older than the latest completed market session.",
  freshness_unverified:
    "Data freshness could not be verified against the NSE holiday calendar, so no actionable signal is shown.",
  incomplete_data: "This score was computed from incomplete data.",
  benchmark_not_fresh: "The NIFTY 50 benchmark data is not current.",
  score_date_after_reference_session: "This score is dated after the reference session.",
  no_reference_session: "No reference market session is available.",
};

export function FreshnessBadge({ data }: { data: FreshnessFields }) {
  if (data.actionable !== false && !data.stale) return null;
  const label = data.stale ? "Stale" : data.freshness_state === "FRESHNESS_UNVERIFIED" ? "Unverified" : "Not actionable";
  const reason = (data.actionable_reason && REASON_TEXT[data.actionable_reason]) || "This score is not currently actionable.";
  const session = data.expected_completed_session ? ` Expected session: ${data.expected_completed_session}.` : "";
  const coverage =
    data.calendar_coverage && data.calendar_coverage !== "COVERED"
      ? ` Calendar coverage: ${data.calendar_coverage}.`
      : "";
  return (
    <Tooltip title={`${reason}${session}${coverage} Shown for reference only.`}>
      <Chip
        label={label}
        size="small"
        color="warning"
        variant="outlined"
        data-testid="freshness-badge"
        sx={{ ml: 0.5, height: 20, fontSize: "0.7rem" }}
      />
    </Tooltip>
  );
}
